import React from 'react';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';
import { Spinner } from './Spinner';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info' | 'loading';

/** Semantic accent per variant. Mirrors Banner's VARIANT_VAR so a success toast
 *  and a success banner read the same. `loading` uses the brand lime accent. */
const VARIANT_VAR: Record<ToastVariant, string> = {
  success: 'var(--success)',
  error: 'var(--danger)',
  warning: 'var(--warning)',
  info: 'var(--info)',
  loading: 'var(--accent)',
};

function VariantIcon({ variant, color }: { variant: ToastVariant; color: string }) {
  if (variant === 'loading') return <Spinner size={18} decorative />;
  const Icon = variant === 'success' ? CheckCircle2 : variant === 'info' ? Info : AlertTriangle;
  return <Icon size={18} style={{ color }} aria-hidden />;
}

export interface ToastProps {
  variant?: ToastVariant;
  message: React.ReactNode;
  /** Optional bold lead line above the message. */
  title?: React.ReactNode;
  /** Drives the enter/leave transition (from react-hot-toast's `t.visible`). */
  visible?: boolean;
  onDismiss?: () => void;
}

/**
 * The on-brand floating toast. Solid surface (readable over any page content),
 * design-system tokens throughout, lucide icon + semantic left accent, dismiss X.
 * Presentational only — lifecycle/positioning is owned by react-hot-toast via the
 * `notify` wrapper in `design-system/toast.tsx`.
 */
export function Toast({ variant = 'info', message, title, visible = true, onDismiss }: ToastProps) {
  const accent = VARIANT_VAR[variant];
  const isError = variant === 'error';
  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      style={{
        display: 'flex',
        alignItems: title ? 'flex-start' : 'center',
        gap: 12,
        width: 'min(380px, calc(100vw - 32px))',
        padding: '12px 14px',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-overlay)',
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${accent}`,
        boxShadow: 'var(--shadow-toast)',
        fontFamily: 'var(--font-sans)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity var(--dur-fast) var(--ease-out), transform var(--dur-fast) var(--ease-out)',
      }}
    >
      <span style={{ color: accent, flex: 'none', display: 'flex', marginTop: title ? 1 : 0 }}>
        <VariantIcon variant={variant} color={accent} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</div>
        )}
        <div
          style={{
            fontSize: title ? 12.5 : 13.5,
            color: title ? 'var(--text-secondary)' : 'var(--text-primary)',
            marginTop: title ? 2 : 0,
            lineHeight: 1.4,
          }}
        >
          {message}
        </div>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            flex: 'none',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            display: 'flex',
            padding: 0,
            marginTop: title ? 1 : 0,
          }}
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}

export default Toast;
